import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";

import { useModalState } from "@cayuse/shared-react.hooks.use-modal-state";
import { IconButton } from "@cayuse/shared-react.components.icon-button";
import { CovertButton } from "@cayuse/shared-react.components.covert-button";
import { Icon } from "@cayuse/shared-react.components.icon";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";

import { useDictionary } from "hooks";
import { useConditionText } from "../hooks/useConditionText";
import { useRuleBuilderContext } from "containers/Rules/context/useRuleBuilderContext";
import { useRuleConditionSelector } from "../utils";

import AndOrComponent from "../AndOrComponent";
import ConditionButton from "../../ConditionButton";

import styles from "./styles.css";

const ConditionOperand = ({
    operand,
    isEditing,
    addOperand,
    index,
    showAddLogicButton = false,
    isEditingOperand,
}) => {
    const dictionary = useDictionary("ruleBuilder");
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const { deleteOperand, addCondition, setEditingOperand } =
        useRuleBuilderContext();
    const { isOpen: isModalOpen, openModal, closeModal } = useModalState(false);

    const handleUpdate = useCallback(
        (updatedValues) => {
            addCondition({ operand, ...updatedValues });
            setEditingOperand(false);
            closeModal();
        },
        [closeModal, addCondition]
    );

    const ruleConditionSelectorModal = useRuleConditionSelector({
        open: isModalOpen,
        onCancel: closeModal,
        onClose: closeModal,
        onUpdate: handleUpdate,
        rule: { operand },
    });

    const handleDeleteConfirm = () => {
        deleteOperand(operand.id);
        setDeleteModalOpen(false);
    };

    const conditionText = useConditionText({
        ...operand.operand,
        ruleType: operand.operand.optionType,
    });

    if (!operand.operand.formElementId) {
        return (
            <>
                <AndOrComponent operand={operand} isEditing={isEditing} index={index}/>
                <ConditionButton operand={operand} />
            </>
        );
    }

    return (
        <>
            <AndOrComponent operand={operand} isEditing={isEditing} index={index}/>
            <div className={styles.operandRow}>
                {conditionText}

                {isEditing && (
                    <div className={styles.buttons}>
                        {showAddLogicButton && (
                            <IconButton
                                secondary
                                labelPosition="left"
                                icon="plus"
                                onClick={addOperand}
                                className={styles.addLogicButton}
                                disabled={isEditingOperand}
                            >
                                {dictionary.conditionBuilder.addLogic ||
                                    "Add Logic"}
                            </IconButton>
                        )}
                        <CovertButton
                            type="button"
                            onClick={openModal}
                            aria-label="Edit"
                            disabled={isEditingOperand}
                        >
                            <Icon name="pencil" size="bigger" />
                        </CovertButton>
                        <CovertButton
                            type="button"
                            onClick={() => setDeleteModalOpen(true)}
                            aria-label="Delete"
                            disabled={isEditingOperand}
                        >
                            <Icon name="trash" size="bigger" />
                        </CovertButton>
                    </div>
                )}

                <AlertDialog
                    title={`${
                        dictionary.conditionBuilder.deleteCondition ||
                        "Delete Condition"
                    }?`}
                    description={
                        <>
                            Delete the condition <b>{conditionText}</b>? Any
                            associated logic will also be deleted.
                        </>
                    }
                    isOpen={isDeleteModalOpen}
                    onConfirm={handleDeleteConfirm}
                    confirmText={
                        dictionary.conditionBuilder.deleteCondition ||
                        "Delete Condition"
                    }
                    onCancel={() => setDeleteModalOpen(false)}
                />
            </div>
            {ruleConditionSelectorModal}
        </>
    );
};

ConditionOperand.propTypes = {
    operand: PropTypes.shape({
        optionId: PropTypes.string,
        optionType: PropTypes.string,
        formElementId: PropTypes.string,
    }),
};

export default ConditionOperand;
